import CopyAll from "@mui/icons-material/CopyAll";
import IconButton from "@mui/material/IconButton";
import {
  GridColDef,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router";
import PageContainer from "../components/Common/PageContainer";
import SectionTable, { NoRowsOverlay } from "../components/Common/SectionTable";
import DashboardLayout from "../components/DashboardLayout/DashboardLayout";
import InternalError from "../components/Errors/InternalError";
import Loading from "../components/Loading";
import {
  useConfigs,
  useDuplicateConfig,
} from "../components/Orion/OrionQueries";
import ConfigListingHeader from "../components/PageHeader/ConfigListing";
import useSession from "../components/useSession/useSession";
import { PipelineConfigAttrs } from "../interfaces/api";

export const ConfigListing: React.FC = () => {
  const { data: configs, isLoading, isError } = useConfigs();
  const { mutate: duplicateConfig } = useDuplicateConfig();
  const { data: session } = useSession();
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const navigate = useNavigate();
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: "updated_date",
      sort: "desc",
    },
  ]);

  function getRowId(config: PipelineConfigAttrs) {
    return config._id;
  }

  const columns: GridColDef<PipelineConfigAttrs>[] = [
    {
      field: "updated_date",
      headerName: "Date",
      flex: 1,
      minWidth: 300,
      type: "dateTime",
      valueGetter: (value, row) => new Date(row.updated_date),
    },
    {
      field: "name",
      headerName: "Nom",

      flex: 5,
      cellClassName: "font-bold",
    },
    {
      field: "workspace",
      headerName: "Espace de travail",
      sortable: false,
      minWidth: 200,
      flex: 1,
      valueGetter: (value, row) => {
        const workspace = session?.workspaces.find(
          (workspace) => workspace._id === row.workspace_id
        );
        if (workspace) {
          return workspace.name;
        }
      },
    },
    {
      field: "copy",
      headerName: "Dupliquer",
      headerAlign: "center",
      align: "center",
      flex: 1,
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={(event) => {
            event.stopPropagation();
            duplicateConfig(params.row._id);
          }}
        >
          <CopyAll />
        </IconButton>
      ),
    },
  ];

  const HeaderComponent = useCallback(
    () => <ConfigListingHeader selection={selection} />,
    [selection]
  );

  return (
    <DashboardLayout>
      <PageContainer slots={{ header: HeaderComponent }} className="max-w-full">
        {isError ? (
          <InternalError />
        ) : isLoading ? (
          <Loading />
        ) : (
          <SectionTable<PipelineConfigAttrs>
            rows={configs ?? []}
            dataGridProps={{
              slots: {
                noRowsOverlay: () => NoRowsOverlay("Aucune configuration"),
              },
              sortModel: sortModel,
              onSortModelChange: (model) => setSortModel(model),
              checkboxSelection: true,
              getRowId: getRowId,
              columns,
              onRowClick: (params) =>
                navigate(`/orion/config/${params.row._id}`),
            }}
            setSelection={setSelection}
          />
        )}
      </PageContainer>
    </DashboardLayout>
  );
};
